import { Component, OnInit } from '@angular/core';
import { AppConst } from '@app/App.const';
import { NavigationService } from '@app/shared/common/navigation/navigation.service';
import { ApiEndpointsService } from '@app/shared/common/services/api-endpoint.service';
import { ApiHttpService } from '@app/shared/common/services/api-http.service';
import { CommonMethodsService } from '@app/shared/common/services/common-methods.service';
import { getCurrentUser } from '@app/shared/common/utils/app-utils';

type HomeLinkCategory = 'ACTION' | 'EXTERNAL' | 'GUIDE' | 'CONTACT' | 'FOOTER';

interface HomeLink {
  key: string;
  label: string;
  url: string;
  icon: string | null;
  hoverLabel: string | null;
  category: HomeLinkCategory;
  type: 'internal-navigation' | 'external-navigation';
  sequence: number;
}

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  standalone: false,
})
export class HomeComponent implements OnInit {
  public userName = '';
  public isLoadingLinks = true;
  public linksLoadError = false;
  public readonly skeletonColumns = [1, 2, 3];
  public readonly skeletonRows = [1, 2, 3, 4];
  public links1: HomeLink[] = [];
  public links2: HomeLink[] = [];
  public guides: HomeLink[] = [];
  public contactLinks: HomeLink[] = [];
  public footerLinks: HomeLink[] = [];

  private readonly iconMap: Record<string, string> = {
    create: '/assets/images/db-check.svg',
    action: '/assets/images/db-check.svg',
    tools: '/assets/images/tools.svg',
    external: '/assets/images/link.svg',
    open: '/assets/images/ic_open.svg',
    arrow: '/assets/images/arrow_forward.svg',
  };

  constructor(
    private navigation: NavigationService,
    private api: ApiHttpService,
    private apiEndpoint: ApiEndpointsService,
    private commonMethodsService: CommonMethodsService
  ) {}

  ngOnInit(): void {
    const currentUser = getCurrentUser();
    this.userName = currentUser?.name ? currentUser.name.split(' ')[0] : '';
    this.loadHomeLinks();
  }

  private loadHomeLinks(): void {
    this.isLoadingLinks = true;
    this.linksLoadError = false;

    this.api
      .callApi(
        AppConst.HttpMethods.POST,
        this.apiEndpoint.getHomeLinks(),
        null
      )
      .subscribe({
        next: (response: HomeLink[]) => {
          this.isLoadingLinks = false;

          if (!Array.isArray(response)) {
            this.linksLoadError = true;
            return;
          }

        const links = Array.isArray(response) ? response : [];
        const sortedLinks = links
          .filter((link) => link?.category && link?.label)
          .sort((first, second) => first.sequence - second.sequence);

        this.links1 = this.getLinksByCategory(sortedLinks, 'ACTION');
        this.links2 = this.getLinksByCategory(sortedLinks, 'EXTERNAL');
        this.guides = this.getLinksByCategory(sortedLinks, 'GUIDE');
        this.contactLinks = this.getLinksByCategory(sortedLinks, 'CONTACT');
        this.footerLinks = this.getLinksByCategory(sortedLinks, 'FOOTER');
        },
        error: () => {
          this.isLoadingLinks = false;
          this.linksLoadError = true;
        },
      });
  }

  private getLinksByCategory(
    links: HomeLink[],
    category: HomeLinkCategory
  ): HomeLink[] {
    return links.filter(
      (link) => link.category?.toUpperCase() === category
    );
  }

  public get contactLink(): HomeLink | undefined {
    return this.contactLinks[0];
  }

  public getMainIcon(link: HomeLink): string {
    return this.getIcon(
      link.icon,
      link.category === 'ACTION' ? 'action' : 'external'
    );
  }

  public getLinkIcon(link: HomeLink): string {
    // The backend icon identifies the main icon. The trailing icon is
    // determined by the link category.
    return link.category === 'ACTION'
      ? this.iconMap['arrow']
      : this.iconMap['open'];
  }

  public getGuideIcon(link: HomeLink): string {
    return this.getIcon(link.icon, 'open');
  }

  public getExternalHref(link: HomeLink): string | null {
    const url = link.url?.trim();
    if (!url) {
      return null;
    }

    if (link.category === 'CONTACT' && !url.includes(':')) {
      return `mailto:${url}`;
    }

    return this.isAllowedExternalUrl(url) ? url : null;
  }

  private getIcon(icon: string | null, fallback: string): string {
    if (!icon) {
      return this.iconMap[fallback];
    }

    return this.iconMap[icon.toLowerCase()] || this.iconMap[fallback];
  }

  public handleClick(link: HomeLink): void {
    if (link.type === 'internal-navigation') {
      this.navigateToInternalLink(link);
      return;
    }

    const url = this.getExternalHref(link);
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  private navigateToInternalLink(link: HomeLink): void {
    // For persona-dependent links, the API returns normal-user|auditor values.
    const routeValues = link.url
      .split('|')
      .map((value) => value.trim())
      .filter(Boolean);

    if (routeValues.length <= 1) {
      this.navigateToRoute(routeValues[0]);
      return;
    }

    this.commonMethodsService.getUserDetailsWithPersona()?.subscribe((user) => {
      const routeValue = user?.persona?.toLowerCase()?.includes('auditor')
        ? routeValues[1]
        : routeValues[0];
      this.navigateToRoute(routeValue);
    });
  }

  private navigateToRoute(routeValue: string | undefined): void {
    if (!routeValue) {
      return;
    }

    const route = this.navigation.getHomeRoute(routeValue);
    if (route) {
      this.navigation.navigateTo(route);
    }
  }

  private isAllowedExternalUrl(url: string): boolean {
    try {
      const parsedUrl = new URL(url);
      return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:';
    } catch {
      return false;
    }
  }
}
